/**
 * Videocall Audio Level Monitoring
 *
 * Supplies levels only to the last-resort MediaRecorder turn-boundary
 * fallback. Streaming interruption decisions belong exclusively to provider
 * VAD on the WebSocket service.
 */
import { getVideocallMicrophoneStream } from './videocallMicrophone';

/**
 * Initialize the audio analyzer for speech detection.
 *
 * @param {object} state  Mutable state object
 * @param {function} state.debugLog
 * @returns {Promise<boolean>}
 */
export async function initializeAudioLevelMonitoring(state) {
    try {
        state.microphoneStream = await getVideocallMicrophoneStream(state.debugLog);

        state.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        state.analyser = state.audioContext.createAnalyser();
        state.microphone = state.audioContext.createMediaStreamSource(state.microphoneStream);

        state.analyser.fftSize = 512;
        state.analyser.smoothingTimeConstant = 0.3;
        const bufferLength = state.analyser.frequencyBinCount;
        state.dataArray = new Uint8Array(bufferLength);

        state.microphone.connect(state.analyser);

        state.debugLog("Audio level monitoring initialized successfully");
        return true;
    } catch (error) {
        console.warn("Could not initialize audio level monitoring:", error);
        return false;
    }
}

/**
 * Get current audio level (0.0 to 1.0) with speech-to-noise weighting.
 *
 * @param {object} state  Must have analyser, dataArray, audioContext
 * @returns {number}
 */
export function getCurrentAudioLevel(state) {
    const { analyser, dataArray, audioContext } = state;
    if (!analyser || !dataArray) return 0;

    analyser.getByteFrequencyData(dataArray);

    let speechSum = 0, noiseSum = 0, speechCount = 0, noiseCount = 0;
    const nyquist = audioContext.sampleRate / 2;
    const binSize = nyquist / dataArray.length;

    for (let i = 0; i < dataArray.length; i++) {
        const freq = i * binSize;
        const amplitude = dataArray[i];
        if (freq >= 500 && freq <= 3400) { speechSum += amplitude; speechCount++; }
        else if (freq < 300 || freq > 4000) { noiseSum += amplitude; noiseCount++; }
    }

    const speechLevel = speechCount > 0 ? (speechSum / speechCount) / 255 : 0;
    const noiseLevel = noiseCount > 0 ? (noiseSum / noiseCount) / 255 : 0;
    const signalToNoise = noiseLevel > 0 ? speechLevel / noiseLevel : speechLevel;
    return Math.min(speechLevel * Math.max(1, signalToNoise * 0.5), 1);
}
